import { useCallback, useEffect, useRef, useState } from "react";
import { ME_COINS, ME_HAND, TABLE_CENTER, seatAnchor } from "./fxAnchors";
import { createId } from "./id";
import type { GameState, Player, Role } from "./types";

/**
 * VEIL has no event stream. Every mutation on the server ends in a single
 * `broadcastState()`, so the client's only signal is a full snapshot — and the
 * game deliberately has no log to read the story out of (decision #1). All
 * narrative therefore has to be recovered by diffing consecutive snapshots,
 * which is what this file does.
 *
 * A doubt is the one story that spans two snapshots rather than one. It opens a
 * loss whose payload is deliberately IDENTICAL on both verdicts for everyone but
 * the seat that owes the card, and the verdict only becomes knowable one
 * snapshot later, when that card flips. Everything either half needs — who
 * claimed, who doubted, over what — therefore has to come off `pendingLoss`,
 * which the server carries for exactly this reason: the window it came out of
 * has already been thrown away by the time the loss opens.
 */
export type FxEffect =
  | { id: string; kind: "coin"; from: string; to: string; count: number }
  | { id: string; kind: "claim"; anchor: string; playerId: string; role: Role }
  | { id: string; kind: "reveal"; anchor: string; playerId: string; role: Role }
  | { id: string; kind: "out"; anchor: string; playerId: string }
  | {
      id: string;
      kind: "challenge";
      claimantId: string;
      challengerId: string;
      loserId: string;
      role: Role;
      proved: boolean;
    }
  /** An accusation with NO verdict attached — which is how every doubt opens,
   *  either way. Neither outcome is the table's to know until somebody
   *  surrenders a card, so this beat carries only who called out whom over
   *  what. The verdict arrives later, as a `challenge`, on the flip. */
  | {
      id: string;
      kind: "callout";
      claimantId: string;
      challengerId: string;
      role: Role;
    }
  /** The proven card going back into the Court deck and its replacement coming
   *  out. `newRole` is filled in only when the viewer is the claimant — every
   *  other seat's hidden cards arrive with `role: null`, which is the point. */
  | {
      id: string;
      kind: "redraw";
      playerId: string;
      anchor: string;
      role: Role;
      newRole: Role | null;
    }
  | { id: string; kind: "victory"; winnerId: string | null };

/** Hard ceiling on the live queue, so a burst can never pile up unbounded. */
const MAX_LIVE = 12;
/** More than this many coins in one move and the extra sprites add nothing. */
const MAX_SPRITES = 5;

export interface GameDeltas {
  effects: FxEffect[];
  dismiss: (id: string) => void;
  flush: () => void;
}

export function useGameDeltas(game: GameState | null, meId: string): GameDeltas {
  const previous = useRef<GameState | null>(null);
  const [effects, setEffects] = useState<FxEffect[]>([]);

  useEffect(() => {
    const before = previous.current;
    previous.current = game;
    if (!game) return;
    // Three snapshots have no story in them and would otherwise fire the whole
    // table at once: a cold start, a different room, and the rematch rewind
    // (`restartGame` resets turn to 1 and re-deals every hand).
    if (!before || before.code !== game.code) return;
    if (game.turn < before.turn) return;
    if (before.phase === "game_over" && game.phase !== "game_over") return;

    const next = readDeltas(before, game, meId);
    if (next.length) setEffects((live) => [...live, ...next].slice(-MAX_LIVE));
  }, [game, meId]);

  const dismiss = useCallback((id: string) => {
    setEffects((live) => live.filter((effect) => effect.id !== id));
  }, []);

  const flush = useCallback(() => setEffects([]), []);

  return { effects, dismiss, flush };
}

function readDeltas(before: GameState, after: GameState, meId: string): FxEffect[] {
  const out: FxEffect[] = [];
  const was = new Map(before.players.map((player) => [player.id, player]));
  // You have no seat pill — PlayerRail filters you out — so your coin badge
  // stands in for your seat, and your hand stands in for your body.
  const purse = (playerId: string) => (playerId === meId ? ME_COINS : seatAnchor(playerId));
  const body = (playerId: string) => (playerId === meId ? ME_HAND : seatAnchor(playerId));

  // ---------------------------------------------------------------- coins ---
  const moves = after.players
    .map((player) => {
      const previous = was.get(player.id);
      return previous ? { id: player.id, delta: player.coins - previous.coins } : null;
    })
    .filter((move): move is { id: string; delta: number } => Boolean(move) && move!.delta !== 0);

  const taker = moves.find((move) => move.delta > 0);
  const giver = moves.find((move) => move.delta < 0);
  if (moves.length === 2 && taker && giver && taker.delta === -giver.delta) {
    // A transfer: Steal, and for free the rollback that reverses one.
    out.push({
      id: createId(),
      kind: "coin",
      from: purse(giver.id),
      to: purse(taker.id),
      count: sprites(taker.delta)
    });
  } else {
    for (const move of moves) {
      out.push(
        move.delta > 0
          ? {
              id: createId(),
              kind: "coin",
              from: TABLE_CENTER,
              to: purse(move.id),
              count: sprites(move.delta)
            }
          : {
              // Paying for a Coup or an Assassination.
              id: createId(),
              kind: "coin",
              from: purse(move.id),
              to: TABLE_CENTER,
              count: sprites(-move.delta)
            }
      );
    }
  }

  // ---------------------------------------------------------------- claim ---
  // A card visibly comes out of whoever claims a role. It looks EXACTLY the
  // same whether the claim is true or a bluff — the client knows its own hand
  // and could trivially compute the difference, and any tell would leak it.
  //
  // Both claims are read off `action`, never off `window`. The window used to be
  // the source and it silently stopped working the moment block and doubt merged
  // into one window: a declared action now opens kind "response", so gating on
  // kind "claim" emitted NOTHING for Tax, Steal, Assassinate or Exchange, while a
  // block — whose own claim window really does open — emitted twice, once when
  // action.blockRole was set and again a snapshot later for the window. `action`
  // carries each claim exactly once, at the instant it is made public.
  const claims: { playerId: string; role: Role }[] = [];
  const action = after.action;
  const declared = Boolean(action && action.id !== before.action?.id);
  if (action?.claimRole && declared) {
    claims.push({ playerId: action.actorId, role: action.claimRole });
  }
  // A block IS a claim, and the only one that lands after its action was
  // declared — hence the second, independent newness test.
  if (action?.blockRole && action.blockerId && (declared || !before.action?.blockRole)) {
    claims.push({ playerId: action.blockerId, role: action.blockRole });
  }
  for (const claim of claims) {
    out.push({
      id: createId(),
      kind: "claim",
      anchor: body(claim.playerId),
      playerId: claim.playerId,
      role: claim.role
    });
  }

  // ------------------------------------------------------------ challenge ---
  // The accusation. `challengeClaim` nulls the window and opens the loss inside
  // one message, and it opens the SAME loss either way as far as any seat but
  // the loser can see, so nothing here may branch on the verdict — a bystander's
  // payload does not contain one. All three fields this needs survive masking
  // intact, which is why the window is not consulted at all any more.
  const opened = after.pendingLoss;
  if (isDoubt(opened) && !sameDoubt(before.pendingLoss, opened)) {
    out.push({
      id: createId(),
      kind: "callout",
      claimantId: opened.claimantId,
      challengerId: opened.challengerId,
      role: opened.claimRole
    });
  }

  // The verdict. The moment the loss clears a card is going face up, and only
  // then does the answer belong to everyone. It has to be rebuilt from the OLD
  // snapshot: the loss it describes is gone from the new one, and is frequently
  // replaced in the very same frame by the next loss the action owes. The two
  // halves can never both fire on one pair — a doubt cannot open and close in
  // the same broadcast.
  const settled = before.pendingLoss;
  if (isDoubt(settled) && !sameDoubt(settled, after.pendingLoss)) {
    const claimantId = settled.claimantId;
    const role = settled.claimRole;
    // Who surrendered IS the verdict, so for anyone but the loser it has to be
    // read off the flip itself — playerId was masked to null for the whole time
    // the loss stood open. Only the two seats in the duel can flip here, and a
    // loss that cleared without one (a seat that left, a rematch) leaves the
    // beat unemitted rather than guessing a verdict.
    const loserId =
      settled.playerId ??
      [claimantId, settled.challengerId].find((playerId) =>
        justFlipped(was.get(playerId), byId(after, playerId))
      ) ??
      null;
    if (loserId) {
      const proved = loserId === settled.challengerId;
      out.push({
        id: createId(),
        kind: "challenge",
        claimantId,
        challengerId: settled.challengerId,
        loserId,
        role,
        proved
      });
      if (proved) {
        // The engine holds the proof swap until this exact frame — swapping it
        // at callout time would renumber the claimant's card ids while the
        // verdict was still secret, which is a tell a bystander can diff — so
        // the replacement is legible here and nowhere earlier.
        out.push({
          id: createId(),
          kind: "redraw",
          playerId: claimantId,
          anchor: body(claimantId),
          role,
          newRole:
            claimantId === meId ? drawnRole(was.get(claimantId), byId(after, claimantId), role) : null
        });
      }
    }
  }

  // ----------------------------------------------------- reveals and outs ---
  for (const player of after.players) {
    const previous = was.get(player.id);
    if (!previous) continue;
    for (const card of player.influence) {
      if (!card.revealed || !card.role) continue;
      const stale = previous.influence.find((old) => old.id === card.id);
      // An id absent from the old hand is a fresh draw or an exchange, never a
      // loss. A card that was already face up is old news.
      if (!stale || stale.revealed) continue;
      out.push({
        id: createId(),
        kind: "reveal",
        anchor: body(player.id),
        playerId: player.id,
        role: card.role
      });
    }
    if (previous.alive && !player.alive) {
      out.push({ id: createId(), kind: "out", anchor: body(player.id), playerId: player.id });
    }
  }

  // -------------------------------------------------------------- victory ---
  if (before.phase !== "game_over" && after.phase === "game_over") {
    out.push({ id: createId(), kind: "victory", winnerId: after.winnerId });
  }

  return out;
}

function sprites(delta: number) {
  return Math.max(1, Math.min(Math.abs(delta), MAX_SPRITES));
}

function byId(state: GameState, playerId: string): Player | undefined {
  return state.players.find((player) => player.id === playerId);
}

/** A loss that came out of a doubt, with the whole duel legible on it. */
type DoubtLoss = NonNullable<GameState["pendingLoss"]> & {
  claimantId: string;
  challengerId: string;
  claimRole: Role;
};

/**
 * All three doubt kinds, deliberately. "bluff" and "failed_challenge" each reach
 * exactly one seat — the one that owes the card — and "callout" is what everyone
 * else gets on EITHER branch, so treating them as one case is the whole point:
 * the accusation beat must be assembled the same way for every viewer.
 */
function isDoubt(loss: GameState["pendingLoss"]): loss is DoubtLoss {
  if (!loss) return false;
  if (loss.kind !== "bluff" && loss.kind !== "failed_challenge" && loss.kind !== "callout") {
    return false;
  }
  return Boolean(loss.claimantId && loss.challengerId && loss.claimRole);
}

/**
 * Is this the same open doubt as before? pendingLoss carries no id, so identity
 * has to be its shape — and it can no longer be `playerId`, which is precisely
 * the field masking nulls out for every viewer who is not the loser. Who
 * claimed, who doubted and over what survive masking untouched and identify the
 * duel on their own.
 *
 * A null check alone cannot do this job: surrendering a card can open the NEXT
 * loss the action owes inside the same broadcast, so `after.pendingLoss` is very
 * often non-null on the very frame one clears.
 */
function sameDoubt(a: GameState["pendingLoss"], b: GameState["pendingLoss"]) {
  if (!a || !b) return false;
  return (
    a.claimantId === b.claimantId &&
    a.challengerId === b.challengerId &&
    a.claimRole === b.claimRole
  );
}

/** Did this seat turn one of its own hidden cards face up between snapshots? */
function justFlipped(before: Player | undefined, after: Player | undefined) {
  if (!before || !after) return false;
  return after.influence.some((card) => {
    if (!card.revealed) return false;
    const stale = before.influence.find((old) => old.id === card.id);
    return Boolean(stale && !stale.revealed);
  });
}

/**
 * The replacement that came out of the Court deck. `proveRole` puts the proven
 * card back in the deck and splices a fresh draw into the same slot, so the swap
 * normally shows up as a hidden card whose id was not in the old hand. Only ever
 * legible in your OWN hand — every other seat's hidden cards arrive with a null
 * role, so this is only ever called for the claimant when the claimant is you.
 *
 * The card goes back with its ORIGINAL id and the deck is then shuffled, so the
 * very same card can be dealt straight back — measured at 8.7% of proofs at two
 * players and 25.9% at six. No new id therefore does not mean no swap; it means
 * the same physical card returned, and the honest render is the role that was
 * just proven. Falling through to null instead painted a face-down card back at
 * your own hand, which is the one lie this flourish exists to prevent.
 */
function drawnRole(
  before: Player | undefined,
  after: Player | undefined,
  proven: Role
): Role | null {
  if (!before || !after) return null;
  const held = new Set(before.influence.map((card) => card.id));
  const drawn = after.influence.find((card) => !card.revealed && !held.has(card.id));
  return drawn?.role ?? proven;
}
